import pako from 'pako'

/**
 * BSrE QR placement relative to "KEPALA DINAS PERUMAHAN" baseline
 * (PDF bottom-left origin) — dipakai STS dan STTS.
 *
 * Calibrated against current templates (QR ~50pt, kanan bawah):
 *   STS  TdY ≈ 213.15 → { xAxis: 466, yAxis: 224, width: 416, height: 174 }
 *   STTS TdY ≈ 199.33 → { xAxis: 466, yAxis: 210, width: 416, height: 160 }
 *
 * Catatan BSrE: width/height = sudut kiri-bawah; xAxis/yAxis = sudut kanan-atas.
 * Ukuran QR = min(xAxis-width, yAxis-height).
 */
const KADIS_QR = {
  size: 50,
  left: 416,
  right: 466,
  /** yAxis ≈ TdY + this */
  topPad: 11
}

function bytesToLatin1 (bytes) {
  let out = ''
  for (let i = 0; i < bytes.length; i++) {
    out += String.fromCharCode(bytes[i])
  }
  return out
}

function findKepalaDinasTdY (content) {
  const match = /([\d.]+)\s+([\d.]+)\s+Td\s+\[\s*\(KEPALA DINAS PERUMAHAN\)\s*\]\s+TJ/.exec(content)
  if (!match) return null
  const y = parseFloat(match[2])
  return Number.isFinite(y) ? y : null
}

function inflateStream (chunk) {
  try {
    return pako.inflate(chunk)
  } catch (e) {
    try {
      return pako.inflateRaw(chunk)
    } catch (e2) {
      return null
    }
  }
}

function coordsFromTdY (tdY) {
  const yAxis = Math.round(tdY + KADIS_QR.topPad)
  const height = Math.round(yAxis - KADIS_QR.size)
  if (yAxis <= height || height < 0) return null
  return {
    page: 1,
    xAxis: KADIS_QR.right,
    yAxis,
    width: KADIS_QR.left,
    height
  }
}

/**
 * Resolve BSrE corner coords from an unsigned STS/STTS PDF blob.
 * Anchors QR under the "KEPALA DINAS PERUMAHAN" title so Y tracks
 * content height (uraian/setoran) across templates.
 * @param {ArrayBuffer|Uint8Array|Blob} pdf
 * @returns {Promise<{page:number,xAxis:number,yAxis:number,width:number,height:number}|null>}
 */
export async function resolveKadisEsignCoordsFromPdf (pdf) {
  let bytes
  if (pdf instanceof Blob) {
    bytes = new Uint8Array(await pdf.arrayBuffer())
  } else if (pdf instanceof ArrayBuffer) {
    bytes = new Uint8Array(pdf)
  } else if (pdf instanceof Uint8Array) {
    bytes = pdf
  } else {
    return null
  }

  const latin1 = bytesToLatin1(bytes)
  let tdY = findKepalaDinasTdY(latin1)

  if (tdY == null) {
    const streamRe = /stream\r?\n/g
    let match
    while ((match = streamRe.exec(latin1)) !== null) {
      const start = match.index + match[0].length
      const end = latin1.indexOf('endstream', start)
      if (end < 0) continue

      let chunk = bytes.subarray(start, end)
      while (
        chunk.length &&
        (chunk[chunk.length - 1] === 10 || chunk[chunk.length - 1] === 13)
      ) {
        chunk = chunk.subarray(0, chunk.length - 1)
      }

      const inflated = inflateStream(chunk)
      if (!inflated) continue

      tdY = findKepalaDinasTdY(bytesToLatin1(inflated))
      if (tdY != null) break
    }
  }

  if (tdY == null) return null
  return coordsFromTdY(tdY)
}
